/**
 * Token Persistence Manager Service
 * 
 * Provides centralized token persistence with navigation awareness for admin authentication.
 * Handles token validation before component mounting, token refresh when approaching expiration,
 * and integrates with existing adminTokenStorage service.
 * 
 * Requirements addressed:
 * - 1.1: Store authentication token with proper persistence
 * - 1.4: Automatically restore token to API service
 * - 3.1: Attempt automatic renewal when token is approaching expiration
 * - 3.2: Validate token freshness and update API service configuration
 */

import { adminTokenStorage, TokenValidationResult } from './adminTokenStorage';
import { apiService } from './api';
import { adminAuthErrorHandler } from './adminAuthErrorHandler';

export interface TokenPersistenceConfig {
  refreshThresholdMinutes?: number; // Minutes before expiration to attempt refresh
  maxRetryAttempts?: number;
  retryDelayMs?: number;
}

export interface TokenValidationOptions {
  skipApiServiceUpdate?: boolean;
  forceRefresh?: boolean;
}

export interface NavigationValidationResult {
  isValid: boolean;
  tokenRefreshed: boolean;
  error?: string;
  shouldRedirectToLogin?: boolean;
}

class TokenPersistenceManager {
  private readonly config: Required<TokenPersistenceConfig>;
  private refreshPromise: Promise<string | null> | null = null;
  private validationInProgress = false;

  constructor(config: TokenPersistenceConfig = {}) {
    this.config = {
      refreshThresholdMinutes: config.refreshThresholdMinutes ?? 5,
      maxRetryAttempts: config.maxRetryAttempts ?? 3,
      retryDelayMs: config.retryDelayMs ?? 1000
    };

    console.log('TokenPersistenceManager: Initialized with config:', this.config);
  }

  /**
   * Store admin token with proper persistence mechanisms
   * Integrates with existing adminTokenStorage service
   */
  async storeToken(token: string, expiresAt?: string, user?: any): Promise<void> {
    try {
      console.log('TokenPersistenceManager: Storing token with persistence');
      
      // Use existing adminTokenStorage for consistent storage
      adminTokenStorage.storeToken(token, expiresAt, user);
      
      // Ensure API service is configured with the token
      apiService.setAdminToken(token);
      
      console.log('TokenPersistenceManager: Token stored and API service configured');
    } catch (error) {
      console.error('TokenPersistenceManager: Failed to store token:', error);
      
      // Handle storage error with error handler
      const storageError = adminAuthErrorHandler.createValidationError(
        'Failed to store token',
        { originalError: error }
      );
      storageError.type = 'STORAGE_ERROR';
      await adminAuthErrorHandler.handleError(storageError);
      
      throw new Error('Failed to store admin token with persistence');
    }
  }

  /**
   * Validate token and refresh if needed before component mounting
   * This is the core method for navigation-aware token validation
   */
  async validateAndRefreshToken(options: TokenValidationOptions = {}): Promise<string | null> {
    if (this.validationInProgress) {
      console.log('TokenPersistenceManager: Validation already in progress, waiting...');
      // Wait for ongoing validation to complete
      await this.waitForValidationComplete();
    }

    this.validationInProgress = true;

    try {
      console.log('TokenPersistenceManager: Starting token validation and refresh check');

      // Check if token exists and is valid
      const validation = adminTokenStorage.validateToken();
      
      if (!validation.isValid) {
        console.log('TokenPersistenceManager: No valid token found:', validation.reason);
        this.validationInProgress = false;
        
        // Handle validation error
        if (validation.isExpired) {
          const error = adminAuthErrorHandler.createTokenExpiredError({ reason: validation.reason });
          await adminAuthErrorHandler.handleError(error, { showNotification: false });
        } else {
          const error = adminAuthErrorHandler.createValidationError(validation.reason || 'Token invalid');
          await adminAuthErrorHandler.handleError(error, { showNotification: false });
        }
        
        return null;
      }

      const token = adminTokenStorage.getToken();
      if (!token) {
        console.log('TokenPersistenceManager: Token validation passed but no token retrieved');
        this.validationInProgress = false;
        return null;
      }

      // Check if token needs refresh
      const needsRefresh = options.forceRefresh || this.shouldRefreshToken(validation);
      
      if (needsRefresh) {
        console.log('TokenPersistenceManager: Token needs refresh, attempting refresh...');
        const refreshedToken = await this.attemptTokenRefresh();
        
        if (refreshedToken) {
          console.log('TokenPersistenceManager: Token refreshed successfully');
          this.validationInProgress = false;
          return refreshedToken;
        } else {
          console.log('TokenPersistenceManager: Token refresh failed, using existing token');
        }
      }

      // Ensure API service has the current token
      if (!options.skipApiServiceUpdate) {
        apiService.setAdminToken(token);
        console.log('TokenPersistenceManager: API service updated with current token');
      }

      this.validationInProgress = false;
      return token;

    } catch (error) {
      console.error('TokenPersistenceManager: Token validation and refresh failed:', error);
      this.validationInProgress = false;
      
      // Handle validation error
      const authError = error instanceof Error ? error : new Error('Token validation failed');
      await adminAuthErrorHandler.handleError(authError, { 
        showNotification: false,
        logError: true 
      });
      
      return null;
    }
  }

  /**
   * Restore token to API service if valid but not set
   * Handles cases where API service loses token during navigation
   */
  async restoreTokenToApiService(): Promise<void> {
    try {
      console.log('TokenPersistenceManager: Restoring token to API service');

      const token = await this.validateAndRefreshToken({ skipApiServiceUpdate: true });
      
      if (token) {
        apiService.setAdminToken(token);
        console.log('TokenPersistenceManager: Token restored to API service successfully');
      } else {
        console.log('TokenPersistenceManager: No valid token to restore');
        apiService.clearAdminToken();
      }
    } catch (error) {
      console.error('TokenPersistenceManager: Failed to restore token to API service:', error);
      apiService.clearAdminToken();
      
      // Handle API service error
      const apiError = adminAuthErrorHandler.createValidationError(
        'Failed to restore token to API service',
        { originalError: error }
      );
      apiError.type = 'API_SERVICE_ERROR';
      await adminAuthErrorHandler.handleError(apiError, { showNotification: false });
    }
  }

  /**
   * Clear stored token and clean up API service
   */
  async clearToken(): Promise<void> {
    try {
      console.log('TokenPersistenceManager: Clearing stored token');
      
      adminTokenStorage.clearToken();
      apiService.clearAdminToken();
      
      // Clear any ongoing refresh operations
      this.refreshPromise = null;
      this.validationInProgress = false;
      
      console.log('TokenPersistenceManager: Token cleared successfully');
    } catch (error) {
      console.error('TokenPersistenceManager: Failed to clear token:', error);
    }
  }

  /**
   * Check if token is valid without triggering refresh
   */
  isTokenValid(): boolean {
    const validation = adminTokenStorage.validateToken();
    return validation.isValid;
  }

  /**
   * Get token expiration time
   */
  getTokenExpirationTime(): Date | null {
    const tokenInfo = adminTokenStorage.getTokenInfo();
    return tokenInfo ? new Date(tokenInfo.expiresAt) : null;
  }

  /**
   * Validate token before navigation to prevent component mounting with invalid auth
   * This is the main method called by navigation context
   */
  async validateBeforeNavigation(): Promise<NavigationValidationResult> {
    try {
      console.log('TokenPersistenceManager: Validating token before navigation');

      const token = await this.validateAndRefreshToken();
      
      if (!token) {
        console.log('TokenPersistenceManager: Navigation validation failed - no valid token');
        return {
          isValid: false,
          tokenRefreshed: false,
          error: 'No valid authentication token',
          shouldRedirectToLogin: true
        };
      }

      // Check if token was refreshed during validation
      const wasRefreshed = this.refreshPromise !== null;

      console.log('TokenPersistenceManager: Navigation validation successful');
      return {
        isValid: true,
        tokenRefreshed: wasRefreshed,
        shouldRedirectToLogin: false
      };

    } catch (error) {
      console.error('TokenPersistenceManager: Navigation validation error:', error);
      
      // Handle navigation validation error
      const authError = error instanceof Error ? error : new Error('Token validation failed');
      const networkError = adminAuthErrorHandler.createNetworkError(authError, { 
        context: 'navigation_validation' 
      });
      await adminAuthErrorHandler.handleError(networkError, { 
        showNotification: true,
        redirectOnFailure: false 
      });
      
      return {
        isValid: false,
        tokenRefreshed: false,
        error: error instanceof Error ? error.message : 'Token validation failed',
        shouldRedirectToLogin: true
      };
    }
  }

  /**
   * Handle concurrent token validation requests
   * Prevents race conditions during navigation
   */
  async handleConcurrentValidation(): Promise<string | null> {
    // If there's already a refresh in progress, wait for it
    if (this.refreshPromise) {
      console.log('TokenPersistenceManager: Concurrent validation detected, waiting for existing refresh');
      return await this.refreshPromise;
    }

    // Otherwise, perform validation normally
    return await this.validateAndRefreshToken();
  }

  /**
   * Get current authentication state for components
   */
  getAuthenticationState(): {
    isAuthenticated: boolean;
    token: string | null;
    expiresAt: Date | null;
    lastValidated: Date | null;
  } {
    const tokenInfo = adminTokenStorage.getTokenInfo();
    const isAuthenticated = adminTokenStorage.isAuthenticated();
    
    return {
      isAuthenticated,
      token: isAuthenticated ? adminTokenStorage.getToken() : null,
      expiresAt: tokenInfo ? new Date(tokenInfo.expiresAt) : null,
      lastValidated: new Date() // Current time as we're checking now
    };
  }

  /**
   * Register callback for token expiration events
   */
  onTokenExpired(callback: () => void): () => void {
    const handleTokenExpired = () => {
      console.log('TokenPersistenceManager: Token expired event triggered');
      callback();
    };

    // Listen for admin logout events which indicate token expiration
    window.addEventListener('admin:logout', handleTokenExpired);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('admin:logout', handleTokenExpired);
    };
  }

  // Private helper methods

  /**
   * Determine if token should be refreshed based on expiration time
   */
  private shouldRefreshToken(validation: TokenValidationResult): boolean {
    if (!validation.isValid || !validation.expiresIn) {
      return false;
    }

    const thresholdMs = this.config.refreshThresholdMinutes * 60 * 1000;
    const shouldRefresh = validation.expiresIn < thresholdMs;
    
    console.log('TokenPersistenceManager: Token refresh check:', {
      expiresInMs: validation.expiresIn,
      thresholdMs,
      shouldRefresh
    });
    
    return shouldRefresh;
  }

  /**
   * Attempt to refresh the token with retry logic
   */
  private async attemptTokenRefresh(): Promise<string | null> {
    // Prevent multiple concurrent refresh attempts
    if (this.refreshPromise) {
      console.log('TokenPersistenceManager: Refresh already in progress, waiting...');
      return await this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh operation
   */
  private async performTokenRefresh(): Promise<string | null> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.config.maxRetryAttempts; attempt++) {
      try {
        console.log(`TokenPersistenceManager: Token refresh attempt ${attempt}/${this.config.maxRetryAttempts}`);

        // Use the API service's refresh method which integrates with adminTokenStorage
        const refreshResult = await (apiService as any).refreshLogManagementToken();
        
        if (refreshResult.success && refreshResult.token) {
          console.log('TokenPersistenceManager: Token refresh successful');
          return refreshResult.token;
        } else {
          throw new Error(refreshResult.error || 'Token refresh failed');
        }

      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown refresh error');
        console.warn(`TokenPersistenceManager: Refresh attempt ${attempt} failed:`, lastError.message);

        // Wait before retrying (except on last attempt)
        if (attempt < this.config.maxRetryAttempts) {
          await this.delay(this.config.retryDelayMs * attempt); // Exponential backoff
        }
      }
    }

    console.error('TokenPersistenceManager: All token refresh attempts failed:', lastError?.message);
    
    // Handle refresh failure
    if (lastError) {
      const refreshError = adminAuthErrorHandler.createRefreshFailedError(
        lastError.message,
        { attempts: this.config.maxRetryAttempts }
      );
      await adminAuthErrorHandler.handleError(refreshError, { 
        showNotification: true,
        attemptRefresh: false 
      });
    }
    
    return null;
  }

  /**
   * Wait for ongoing validation to complete
   */
  private async waitForValidationComplete(): Promise<void> {
    const maxWaitMs = 5000; // 5 seconds max wait
    const checkIntervalMs = 100;
    let waitedMs = 0;

    while (this.validationInProgress && waitedMs < maxWaitMs) {
      await this.delay(checkIntervalMs);
      waitedMs += checkIntervalMs;
    }

    if (this.validationInProgress) {
      console.warn('TokenPersistenceManager: Validation wait timeout, proceeding anyway');
      this.validationInProgress = false;
    }
  }

  /**
   * Utility method for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const tokenPersistenceManager = new TokenPersistenceManager();

// Types are already exported above with their definitions